import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { useFormikContext } from "formik";
import { useCurrentChatGroupContext } from "hooks/useCurrentChatGroupContext";
import { MultiValue, SingleValue } from "react-select";
import { useGetCountryStatesQuery } from "store/apis/teamSetting";
import { CustomField } from "../customField.types";
import { OnLabelResolved } from "../customFieldSelector.types";
import {
  getSelectedComboboxOption,
  resolveSelectedOptionLabel,
} from "../customFieldSelector.utils";
import { useGetCountriesDataQuery } from "store/apis/onboarding";

const UNITED_STATES_COUNTRY_NAME = "United States";

type StateSelectorFieldProps = {
  field: CustomField;
  name: string;
  countryFieldName?: string;
  entityFieldName?: string;
  entityIdFromProps?: string;
  disabled?: boolean;
  onLabelResolved?: OnLabelResolved;
};

export const StateSelectorField = ({
  field,
  name,
  countryFieldName,
  entityFieldName,
  entityIdFromProps,
  disabled,
  onLabelResolved,
}: StateSelectorFieldProps) => {
  const { values } = useFormikContext<Record<string, string | string[]>>();
  const { entities } = useCurrentChatGroupContext();
  const { data: countries = [] } = useGetCountriesDataQuery();

  const countryFieldValue = countryFieldName ? values[countryFieldName] : "";
  const selectedCountryId = Array.isArray(countryFieldValue)
    ? ""
    : countryFieldValue;
  const entityFieldValue = entityFieldName ? values[entityFieldName] : "";
  const resolvedEntityId =
    (!Array.isArray(entityFieldValue) && entityFieldValue) || entityIdFromProps;
  const entityCountryId = entities.find(
    (entity) => entity.uuid === resolvedEntityId
  )?.country_id;
  const defaultCountryId = countries.find(
    (country) => country.name === UNITED_STATES_COUNTRY_NAME
  )?.uuid;
  const countryId =
    selectedCountryId || entityCountryId || defaultCountryId || "";

  const { data: states = [], isLoading } = useGetCountryStatesQuery(
    { countryId },
    { skip: !countryId }
  );

  const options: OptionData[] = states.map(({ name: stateName, state_id }) => ({
    label: stateName,
    value: state_id,
  }));
  const selectedOption = getSelectedComboboxOption(
    options,
    values[name],
    field.is_multi_select
  );

  const handleChange = (
    option: SingleValue<OptionData> | MultiValue<OptionData>
  ) => {
    const label = resolveSelectedOptionLabel(option);
    if (label) {
      onLabelResolved?.(name, label);
    }
  };

  return (
    <Combobox
      withForm
      name={name}
      label={field.label}
      placeholder={field.placeholder}
      required={field.is_required}
      tooltipText={field.tooltip}
      options={options}
      value={selectedOption}
      onChange={handleChange}
      isLoading={isLoading}
      isMulti={field.is_multi_select}
      isDisabled={disabled || !countryId}
      menuPortalTarget={document.body}
    />
  );
};
